import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { login as apiLogin } from '../api/franchise.api';

interface AuthState {
  token: string | null;
  franchiseId: string | null;
  franchiseName: string | null;
}

interface AuthContextValue extends AuthState {
  isAuthenticated: boolean;
  login: (name: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(() => ({
    token: localStorage.getItem('franchise_token'),
    franchiseId: localStorage.getItem('franchise_id'),
    franchiseName: localStorage.getItem('franchise_name'),
  }));

  const login = useCallback(async (name: string, password: string) => {
    const res = await apiLogin(name, password);
    localStorage.setItem('franchise_token', res.accessToken);
    localStorage.setItem('franchise_id', String(res.franchiseId));
    localStorage.setItem('franchise_name', name);
    setAuth({
      token: res.accessToken,
      franchiseId: res.franchiseId,
      franchiseName: name,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('franchise_token');
    localStorage.removeItem('franchise_id');
    localStorage.removeItem('franchise_name');
    setAuth({ token: null, franchiseId: null, franchiseName: null });
  }, []);

  const value: AuthContextValue = {
    ...auth,
    isAuthenticated: !!auth.token && !!auth.franchiseId,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
